import Vue from 'vue';
import VueI18n from 'vue-i18n';
import { messages } from './messages';

Vue.use(VueI18n);

const DEFAULT_LANGUAGE = 'zh';
export const SUPPORTED_LANGUAGES = ['zh', 'en'];
export const LANGUAGE_STORAGE_KEY = 'aihelp_language';
const LOCALIZABLE_ATTRS = ['placeholder', 'title', 'aria-label'];
const SKIP_TAGS = new Set(['SCRIPT', 'STYLE', 'PRE', 'CODE', 'TEXTAREA']);

function getLocationSearch() {
  if (typeof window === 'undefined') {
    return '';
  }

  return window.location.search || '';
}

export function normalizeLanguage(language) {
  if (!language || typeof language !== 'string') {
    return DEFAULT_LANGUAGE;
  }

  const normalized = language.toLowerCase();
  if (normalized.startsWith('en')) {
    return 'en';
  }

  return DEFAULT_LANGUAGE;
}

export function resolveLanguageFromQuery(search = getLocationSearch()) {
  const params = new URLSearchParams(search || '');
  if (!params.has('language')) {
    return null;
  }

  return normalizeLanguage(params.get('language'));
}

export function getStoredLanguage() {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    const storedLanguage = window.localStorage.getItem(LANGUAGE_STORAGE_KEY);
    return storedLanguage ? normalizeLanguage(storedLanguage) : null;
  } catch (error) {
    return null;
  }
}

export function setStoredLanguage(language) {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.localStorage.setItem(LANGUAGE_STORAGE_KEY, normalizeLanguage(language));
  } catch (error) {
    // Ignore storage errors and keep the app usable.
  }
}

export function resolveLanguage(search = getLocationSearch()) {
  const languageFromQuery = resolveLanguageFromQuery(search);
  if (languageFromQuery) {
    return languageFromQuery;
  }

  return getStoredLanguage() || DEFAULT_LANGUAGE;
}

export function interpolateMessage(template, params = {}) {
  return String(template).replace(/\{(\w+)\}/g, (_, key) => {
    const value = params[key];
    return value === undefined || value === null ? `{${key}}` : value;
  });
}

export function translateLiteral(i18n, source, params = {}) {
  if (!source || typeof source !== 'string') {
    return source;
  }

  const localeMessages = i18n.getLocaleMessage(i18n.locale) || {};
  const legacy = localeMessages.legacy || {};
  const translated = legacy[source];

  return interpolateMessage(translated || source, params);
}

function shouldSkipElement(element) {
  if (!element || element.nodeType !== Node.ELEMENT_NODE) {
    return false;
  }

  if (SKIP_TAGS.has(element.tagName)) {
    return true;
  }

  if (element.dataset && element.dataset.i18nSkip === 'true') {
    return true;
  }

  return !!element.closest('.markdown-body');
}

function localizeTextNode(node, i18n) {
  const rawText = node.nodeValue;
  if (!rawText || !rawText.trim()) {
    return;
  }

  const trimmed = rawText.trim();
  const translated = translateLiteral(i18n, trimmed);
  if (translated === trimmed) {
    return;
  }

  const start = rawText.indexOf(trimmed);
  node.nodeValue = `${rawText.slice(0, start)}${translated}${rawText.slice(start + trimmed.length)}`;
}

function localizeAttributes(element, i18n) {
  LOCALIZABLE_ATTRS.forEach((attr) => {
    if (!element.hasAttribute(attr)) {
      return;
    }

    const rawValue = element.getAttribute(attr);
    if (!rawValue || !rawValue.trim()) {
      return;
    }

    const translated = translateLiteral(i18n, rawValue.trim());
    if (translated !== rawValue.trim()) {
      element.setAttribute(attr, translated);
    }
  });
}

export function localizeElement(root, i18n) {
  if (!root || !i18n || typeof document === 'undefined') {
    return;
  }

  const walk = (node) => {
    if (!node) {
      return;
    }

    if (node.nodeType === Node.TEXT_NODE) {
      localizeTextNode(node, i18n);
      return;
    }

    if (node.nodeType !== Node.ELEMENT_NODE) {
      return;
    }

    if (shouldSkipElement(node)) {
      return;
    }

    localizeAttributes(node, i18n);
    Array.from(node.childNodes).forEach(walk);
  };

  walk(root);
}

function syncDocumentLanguage(language) {
  if (typeof document === 'undefined') {
    return;
  }

  document.documentElement.setAttribute('lang', normalizeLanguage(language));
}

function syncLanguageQueryParam(language) {
  if (typeof window === 'undefined' || !window.history || !window.location) {
    return;
  }

  const nextLocale = normalizeLanguage(language);
  const url = new URL(window.location.href);
  url.searchParams.set('language', nextLocale);
  window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
}

const locale = resolveLanguage();

const i18n = new VueI18n({
  locale,
  fallbackLocale: DEFAULT_LANGUAGE,
  silentTranslationWarn: true,
  silentFallbackWarn: true,
  messages
});

export function setLanguage(language, options = {}) {
  const { syncUrl = true } = options;
  const nextLocale = normalizeLanguage(language);

  if (i18n.locale !== nextLocale) {
    i18n.locale = nextLocale;
  }

  setStoredLanguage(nextLocale);
  syncDocumentLanguage(nextLocale);

  if (syncUrl) {
    syncLanguageQueryParam(nextLocale);
  }

  return nextLocale;
}

setLanguage(locale, { syncUrl: false });

export default i18n;
